import { useState, useEffect } from 'react';
import { useAdminTheme } from '@/context/ThemeContext';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  Search,
  ChevronRight,
  RefreshCw,
  User,
  Building,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Loader2,
  Filter,
  ArrowUpRight,
  Send,
  Eye,
  GraduationCap,
  DollarSign,
  MapPin,
  Mail,
  Phone,
  FileCheck,
  History,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';

const API_URL = window.location.origin;

// Stage colors for badges
const STAGE_COLORS = {
  applied: 'bg-blue-100 text-blue-700',
  docs_review: 'bg-orange-100 text-orange-700',
  docs_complete: 'bg-green-100 text-green-700',
  uaf_pending: 'bg-purple-100 text-purple-700',
  uaf_complete: 'bg-green-100 text-green-700',
  submitted_to_uni: 'bg-blue-100 text-blue-700',
  under_review: 'bg-orange-100 text-orange-700',
  offer_received: 'bg-green-100 text-green-700',
  offer_accepted: 'bg-green-100 text-green-700',
  fee_payment: 'bg-purple-100 text-purple-700',
  coe_issued: 'bg-green-100 text-green-700',
  visa_applied: 'bg-blue-100 text-blue-700',
  visa_granted: 'bg-emerald-100 text-emerald-700',
  visa_rejected: 'bg-red-100 text-red-700',
  offer_rejected: 'bg-red-100 text-red-700'
};

const ApplicationsManager = () => {
  const { isDark } = useAdminTheme();
  const { token, logout } = useCMSAuth();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [stages, setStages] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [stageNotes, setStageNotes] = useState('');
  const [noteText, setNoteText] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadStages();
    loadAllApplications();
  }, []);

  const loadStages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/applications/stages`);
      if (res.ok) {
        const data = await res.json();
        setStages(data.stages || []);
      }
    } catch (error) {
      console.error('Error loading stages:', error);
    }
  };

  const loadAllApplications = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/applications/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.status === 401) {
        toast.error('Session expired. Please log in again.');
        logout();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setApplications(data.applications || []);
        setStats(data.stats || {});
      } else {
        toast.error('Failed to load applications');
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStage = async () => {
    if (!selectedApp || !newStage) return;
    
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/applications/${selectedApp.application_id}/stage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          stage_id: newStage,
          notes: stageNotes || null
        })
      });

      if (res.status === 401) {
        toast.error('Session expired. Please log in again.');
        logout();
        return;
      }
      if (res.ok) {
        toast.success(`Application moved to "${stages.find(s => s.id === newStage)?.name}"`);
        setIsUpdateDialogOpen(false);
        setNewStage('');
        setStageNotes('');
        loadAllApplications();
      } else {
        toast.error('Failed to update stage');
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Failed to update stage');
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async () => {
    if (!selectedApp || !noteText.trim()) return;
    
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/applications/${selectedApp.application_id}/note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          note: noteText,
          is_internal: true
        })
      });

      if (res.status === 401) {
        toast.error('Session expired. Please log in again.');
        logout();
        return;
      }
      if (res.ok) {
        toast.success('Note added successfully');
        setIsNoteDialogOpen(false);
        setNoteText('');
        loadAllApplications();
      } else {
        toast.error('Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setUpdating(false);
    }
  };

  const getNextStages = (currentStage) => {
    const current = stages.find(s => s.id === currentStage);
    if (!current) return stages;
    return stages.filter(s => s.order > current.order || s.is_final);
  };

  const loadApplicationDetails = async (app) => {
    setSelectedApp(app);
    setIsDetailsDialogOpen(true);
    setLoadingDetails(true);
    setApplicationDetails(null);
    
    try {
      // Fetch full application details including student info, documents, and history
      const [appRes, studentRes] = await Promise.all([
        fetch(`${API_URL}/api/applications/${app.application_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/students/${app.user_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null) // Student info might not be available
      ]);
      
      let details = { ...app };
      
      if (appRes.ok) {
        const appData = await appRes.json();
        details = { ...details, ...appData };
      }
      
      if (studentRes?.ok) {
        const studentData = await studentRes.json();
        details.student = studentData;
      }
      
      setApplicationDetails(details);
    } catch (error) {
      console.error('Error loading application details:', error);
      // Still show basic info even if fetch fails
      setApplicationDetails(app);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.university_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.program?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStage = stageFilter === 'all' || app.current_stage === stageFilter;
    
    return matchesSearch && matchesStage;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={`p-6 ${isDark ? 'bg-[#0f1a1f]' : 'bg-slate-50'} min-h-screen`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            Applications Management
          </h1>
          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            Manage student application lifecycle stages
          </p>
        </div>
        <Button onClick={loadAllApplications} variant="outline" className="rounded-full">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-ux-cyan">{stats.total || 0}</div>
            <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Total</div>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-500">{stats.active || 0}</div>
            <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Active</div>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-500">{stats.pending_docs || 0}</div>
            <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Pending Docs</div>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-emerald-500">{stats.offers || 0}</div>
            <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Offers</div>
          </CardContent>
        </Card>
        <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-500">{stats.visas || 0}</div>
            <div className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Visas Granted</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className={`mb-6 ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
              <Input
                placeholder="Search by university, student ID, or program..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
              />
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className={`w-full md:w-[200px] ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}>
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {stages.map(stage => (
                  <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'border-white/10' : ''}>
                <TableHead className={isDark ? 'text-white/70' : ''}>Application</TableHead>
                <TableHead className={isDark ? 'text-white/70' : ''}>Student</TableHead>
                <TableHead className={isDark ? 'text-white/70' : ''}>Stage</TableHead>
                <TableHead className={isDark ? 'text-white/70' : ''}>Updated</TableHead>
                <TableHead className={`text-right ${isDark ? 'text-white/70' : ''}`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <FileText className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
                    <p className={isDark ? 'text-white/40' : 'text-slate-400'}>No applications found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredApplications.map((app) => (
                  <TableRow 
                    key={app.application_id} 
                    className={`${isDark ? 'border-white/10' : ''} cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 transition-colors`}
                    onClick={() => loadApplicationDetails(app)}
                    data-testid={`app-row-${app.application_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
                          <Building className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {app.university_name}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                            {app.program} • {app.intake}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                        <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          {app.user_id?.slice(0, 15)}...
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STAGE_COLORS[app.current_stage] || 'bg-slate-100 text-slate-700'}`}>
                        {stages.find(s => s.id === app.current_stage)?.name || app.current_stage}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                        <span className={isDark ? 'text-white/60' : 'text-slate-500'}>
                          {formatDate(app.updated_at)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => loadApplicationDetails(app)}
                          className="rounded-full"
                          data-testid={`view-btn-${app.application_id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedApp(app);
                            setIsNoteDialogOpen(true);
                          }}
                          className="rounded-full"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedApp(app);
                            setNewStage(app.current_stage);
                            setIsUpdateDialogOpen(true);
                          }}
                          className="bg-ux-cyan hover:bg-ux-cyan/90 text-white rounded-full"
                        >
                          <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Update
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Update Stage Dialog */}
      <Dialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
        <DialogContent className={isDark ? 'bg-[#1a2830] border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Update Application Stage</DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              Move "{selectedApp?.university_name}" to a new stage
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                Current Stage
              </label>
              <Badge className={`ml-2 ${STAGE_COLORS[selectedApp?.current_stage] || ''}`}>
                {stages.find(s => s.id === selectedApp?.current_stage)?.name}
              </Badge>
            </div>
            
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                Move to Stage
              </label>
              <Select value={newStage} onValueChange={setNewStage}>
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}>
                  <SelectValue placeholder="Select new stage" />
                </SelectTrigger>
                <SelectContent>
                  {getNextStages(selectedApp?.current_stage).map(stage => (
                    <SelectItem key={stage.id} value={stage.id}>
                      <div className="flex items-center gap-2">
                        {stage.is_final && stage.is_success && <CheckCircle className="w-4 h-4 text-green-500" />}
                        {stage.is_final && !stage.is_success && <XCircle className="w-4 h-4 text-red-500" />}
                        {!stage.is_final && <ChevronRight className="w-4 h-4 text-blue-500" />}
                        {stage.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                Notes (Optional)
              </label>
              <Textarea
                value={stageNotes}
                onChange={(e) => setStageNotes(e.target.value)}
                placeholder="Add any notes about this stage change..."
                className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUpdateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={updateApplicationStage}
              disabled={updating || !newStage || newStage === selectedApp?.current_stage}
              className="bg-ux-cyan hover:bg-ux-cyan/90 text-white"
            >
              {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Update Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent className={isDark ? 'bg-[#1a2830] border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Add Internal Note</DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              Add a note to "{selectedApp?.university_name}" application
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
              rows={4}
              className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
            />
            <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              This note will be visible to counselors only
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={addNote}
              disabled={updating || !noteText.trim()}
              className="bg-ux-cyan hover:bg-ux-cyan/90 text-white"
            >
              {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className={`max-w-3xl max-h-[85vh] overflow-y-auto ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <FileText className="w-5 h-5 text-ux-cyan" />
              Application Details
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              Full details for this application record
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
            </div>
          ) : applicationDetails ? (
            <div className="space-y-6 py-4">
              {/* Application Info Section */}
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <GraduationCap className="w-4 h-4 text-ux-cyan" /> Application Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>University</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {applicationDetails.university_name}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Program</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {applicationDetails.program}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Intake</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {applicationDetails.intake || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Current Stage</p>
                    <Badge className={`${STAGE_COLORS[applicationDetails.current_stage] || 'bg-slate-100 text-slate-700'}`}>
                      {stages.find(s => s.id === applicationDetails.current_stage)?.name || applicationDetails.current_stage}
                    </Badge>
                  </div>
                  {applicationDetails.tuition_fee && (
                    <div>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Tuition Fee</p>
                      <p className={`text-sm font-medium flex items-center gap-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <DollarSign className="w-3.5 h-3.5" /> {applicationDetails.tuition_fee}
                      </p>
                    </div>
                  )}
                  {applicationDetails.match_score && (
                    <div>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>AI Match Score</p>
                      <p className={`text-sm font-medium text-emerald-600`}>
                        {applicationDetails.match_score}%
                      </p>
                    </div>
                  )}
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Application ID</p>
                    <p className={`text-xs font-mono ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                      {applicationDetails.application_id}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Status</p>
                    <p className={`text-sm font-medium capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {applicationDetails.status || 'active'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Student Info Section */}
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <User className="w-4 h-4 text-ux-cyan" /> Student Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Student ID</p>
                    <p className={`text-xs font-mono ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                      {applicationDetails.user_id}
                    </p>
                  </div>
                  {applicationDetails.student && (
                    <>
                      <div>
                        <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Name</p>
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {applicationDetails.student.name || applicationDetails.student.full_name || 'N/A'}
                        </p>
                      </div>
                      {applicationDetails.student.email && (
                        <div>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Email</p>
                          <p className={`text-sm flex items-center gap-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            <Mail className="w-3.5 h-3.5" /> {applicationDetails.student.email}
                          </p>
                        </div>
                      )}
                      {applicationDetails.student.phone && (
                        <div>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Phone</p>
                          <p className={`text-sm flex items-center gap-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            <Phone className="w-3.5 h-3.5" /> {applicationDetails.student.phone}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Stage History Section */}
              {applicationDetails.stage_history && applicationDetails.stage_history.length > 0 && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                  <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <History className="w-4 h-4 text-ux-cyan" /> Stage History
                  </h3>
                  <div className="space-y-3">
                    {applicationDetails.stage_history.map((history, idx) => (
                      <div key={idx} className={`flex items-start gap-3 ${idx !== 0 ? 'pt-3 border-t' : ''} ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                          <ChevronRight className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-slate-500'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={`${STAGE_COLORS[history.stage_id] || 'bg-slate-100 text-slate-700'} text-xs`}>
                              {stages.find(s => s.id === history.stage_id)?.name || history.stage_id}
                            </Badge>
                            <span className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                              {formatDate(history.changed_at)}
                            </span>
                          </div>
                          {history.notes && (
                            <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                              {history.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {applicationDetails.documents && applicationDetails.documents.length > 0 && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                  <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <FileCheck className="w-4 h-4 text-ux-cyan" /> Attached Documents
                  </h3>
                  <div className="space-y-2">
                    {applicationDetails.documents.map((doc, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          <FileText className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            {doc.doc_type || doc.name || `Document ${idx + 1}`}
                          </span>
                        </div>
                        {doc.url && (
                          <Button size="sm" variant="ghost" asChild className="h-7">
                            <a href={doc.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes Section */}
              {applicationDetails.notes && applicationDetails.notes.length > 0 && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                  <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <MessageSquare className="w-4 h-4 text-ux-cyan" /> Internal Notes
                  </h3>
                  <div className="space-y-2">
                    {applicationDetails.notes.map((note, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{note.note}</p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                          {formatDate(note.created_at)} {note.created_by && `• ${note.created_by}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timestamps Section */}
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Clock className="w-4 h-4 text-ux-cyan" /> Timestamps
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Created</p>
                    <p className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                      {formatDate(applicationDetails.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Last Updated</p>
                    <p className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                      {formatDate(applicationDetails.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
              <p className={isDark ? 'text-white/40' : 'text-slate-400'}>Failed to load application details</p>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsDetailsDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setIsDetailsDialogOpen(false);
                setNewStage(selectedApp?.current_stage);
                setIsUpdateDialogOpen(true);
              }}
              className="bg-ux-cyan hover:bg-ux-cyan/90 text-white"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" /> Update Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ApplicationsManager;
